import { IcBT, IcTro } from '@assets/icons/40x40';
import { SensorOrSubSensorInfoByType } from '@components/cockpit/panels/monitoring/monitoring.types';
import DataSheetCard from '@components/common/layout/DataSheetCard';
import { appIntl } from '@components/locale/IntlGlobalProvider';
import { useAutomatonCommand } from '@hooks/useAutomatonCommand';
import { getCabinetSectionStatuses, getCabinetStatuses, isSectionRepulsionOn } from '@utils/sensors/sensors.utils';

import { SensorTypeEnum, SpecificStateEnum } from '@/types/sensor/sensors';

export default function AutomatonCommands({
  status,
  configuration,
}: Readonly<SensorOrSubSensorInfoByType<SensorTypeEnum.AUTOMATON>>) {
  const { formatMessage } = appIntl();

  const cabinetStatuses = getCabinetStatuses(status);
  const sectionStatuses = cabinetStatuses.flatMap((cabinetStatus) => getCabinetSectionStatuses(cabinetStatus));

  const useAutomaton = useAutomatonCommand({
    automatonStatus: status,
    automatonConfiguration: configuration,
  });

  if (!useAutomaton.canCommand) {
    return null;
  }

  const countDetection = sectionStatuses.filter((segmentStatus) => !isSectionRepulsionOn(segmentStatus)).length;
  const countAwake = cabinetStatuses.filter(
    (cabinetsStatus) => cabinetsStatus.specificState !== SpecificStateEnum.SLEEP,
  ).length;

  const sleepActions = [
    {
      action: 'on',
      i18nKey: 'sensors.tab.commands.cabinet.sleep.on',
    },
    {
      action: 'off',
      i18nKey: 'sensors.tab.commands.cabinet.sleep.off',
    },
  ];

  const defenceActions = [
    {
      action: 'detection',
      i18nKey: 'sensors.tab.commands.automaton.defence.detection',
      disabled: !useAutomaton.canDetect,
    },
    {
      action: 'repulsion',
      i18nKey: 'sensors.tab.commands.automaton.defence.repulsion',
      disabled: !useAutomaton.canRepulse,
    },
  ];

  const canRepulseOrDetect = useAutomaton.canRepulse || useAutomaton.canDetect;

  return (
    <>
      <DataSheetCard
        icon={IcBT}
        description={formatMessage({ id: 'sensors.tab.commands.automaton.sleep.description' })}
        subDescription={formatMessage(
          { id: 'sensors.tab.commands.automaton.sleep.counter' },
          { countAwake, total: cabinetStatuses.length },
        )}
        actions={sleepActions}
        isDisabled={!status}
        isLoading={useAutomaton.isSleepAllCommandLoading}
        onClick={(action) => useAutomaton.sleepAll(action === 'on')}
      />
      {canRepulseOrDetect && (
        <DataSheetCard
          icon={IcTro}
          description={formatMessage({ id: 'sensors.tab.commands.automaton.defence.description' })}
          subDescription={formatMessage(
            { id: 'sensors.tab.commands.automaton.defence.counter' },
            { countDetection, total: sectionStatuses.length },
          )}
          actions={defenceActions}
          isDisabled={!status}
          isLoading={useAutomaton.isRepulsionCommandLoading}
          onClick={(action) => useAutomaton.repulse(action !== 'detection')}
        />
      )}
    </>
  );
}
